/**
 * Durable document-evidence lane for visual material embedded in PDFs.
 *
 * PDF XObjects and bounded whole-page recovery renders are transient transport
 * inputs. They must never be promoted into the customer's project-photo
 * collection or persisted as data URLs. This service consumes every unresolved
 * visual through vision in bounded request batches and returns only grounded
 * text plus a byte-free receipt suitable for documents[].
 */

import { classifyAiServiceFailure } from './ai-service-failure';

type LooseRecord = Record<string, any>;

type VisualCandidate = {
    source: 'pdf-embed' | 'pdf-page-fallback';
    page: number;
    imageId: string;
    width: number | null;
    height: number | null;
    label: string;
    dataUrl: string | null;
};

type DurableVisualIdentity = Omit<VisualCandidate, 'dataUrl'>;

const VISUAL_SOURCES = new Set(['pdf-embed', 'pdf-page-fallback']);
// Six images is a transport batch, not an ingestion ceiling. The loop below
// continues until every selected visual has been attempted.
const VISION_BATCH_SIZE = 6;
const INLINE_MEDIA_OMITTED = '[inline media omitted]';

function positivePage(value: unknown): number | null {
    const page = Number(value);
    return Number.isInteger(page) && page > 0 ? page : null;
}

function finiteDimension(value: unknown): number | null {
    const dimension = Number(value);
    return Number.isFinite(dimension) && dimension > 0 ? dimension : null;
}

function containsInlineMediaMarker(value: string): boolean {
    let markerProbe = value;
    // Decode only printable ASCII into this private probe. The durable output
    // remains untouched unless the complete value is rejected. Repeating the
    // pass also handles JSON strings whose escape sequences were themselves
    // escaped by an intermediate provider/gateway.
    for (let pass = 0; pass < 4; pass += 1) {
        markerProbe = markerProbe
            .replace(/&(amp|colon|sol|bsol|tab|newline);/gi, (_token, name) => ({
                amp: '&',
                colon: ':',
                sol: '/',
                bsol: '\\',
                tab: '\t',
                newline: '\n',
            }[String(name).toLowerCase()] || _token))
            .replace(/\\u\{([0-9a-f]{1,6})\}/gi, (token, hex) => {
                const code = Number.parseInt(hex, 16);
                return code >= 0x20 && code <= 0x7e ? String.fromCharCode(code) : token;
            })
            .replace(/\\u([0-9a-f]{4})/gi, (token, hex) => {
                const code = Number.parseInt(hex, 16);
                return code >= 0x20 && code <= 0x7e ? String.fromCharCode(code) : token;
            })
            .replace(/%u([0-9a-f]{4})/gi, (token, hex) => {
                const code = Number.parseInt(hex, 16);
                return code >= 0x20 && code <= 0x7e ? String.fromCharCode(code) : token;
            })
            .replace(/(?:\\x|%)([0-9a-f]{2})/gi, (token, hex) => {
                const code = Number.parseInt(hex, 16);
                return code >= 0x20 && code <= 0x7e ? String.fromCharCode(code) : token;
            })
            .replace(/&#x([0-9a-f]{1,6});?/gi, (token, hex) => {
                const code = Number.parseInt(hex, 16);
                return code >= 0x20 && code <= 0x7e ? String.fromCharCode(code) : token;
            })
            .replace(/&#([0-9]{1,3});?/g, (token, digits) => {
                const code = Number.parseInt(digits, 10);
                return code >= 0x20 && code <= 0x7e ? String.fromCharCode(code) : token;
            })
            .replace(/\\([\\/:])/g, '$1');
    }
    // Whitespace folding is a valid way for a provider to wrap a long data
    // URL, including between letters. Removing it only in the private probe
    // makes `d a t a \\: image \\/ png` as unsafe as `data:image/png`.
    markerProbe = markerProbe
        .replace(/[\s\u200b-\u200f\u2060\ufeff]+/g, '')
        .replace(/\\([\\/:])/g, '$1');
    return /data:(?:image|video)\//i.test(markerProbe);
}

function cleanText(value: unknown): string {
    // Provider text is durable. If a provider ever echoes an inline-media URL,
    // discard the whole response rather than trying to find the end of its
    // payload: folded base64 has no trustworthy boundary from following prose.
    // Normalise the common escaped and percent-encoded separators before the
    // marker check so neither representation can reintroduce customer bytes.
    const text = String(value || '');
    if (containsInlineMediaMarker(text)) {
        return INLINE_MEDIA_OMITTED;
    }
    return text.trim();
}

function compactError(value: unknown): string {
    return cleanText((value as { message?: unknown })?.message || value || 'visual analysis returned no content')
        .slice(0, 500);
}

function escapeXmlAttribute(value: unknown): string {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

function durableIdentity(candidate: VisualCandidate): DurableVisualIdentity {
    const { dataUrl: _dataUrl, ...durable } = candidate;
    return durable;
}

function normaliseVisual(value: unknown, index: number): VisualCandidate | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const visual = value as LooseRecord;
    const source = String(visual.source || '').trim().toLowerCase();
    const page = positivePage(visual.sourcePdfPage ?? visual.page);
    const imageId = String(visual.sourcePdfImageId || visual.imageId || '').trim();
    if (!VISUAL_SOURCES.has(source) || !page || !imageId || containsInlineMediaMarker(imageId)) return null;

    const data = visual.data ?? visual.dataUrl ?? visual.url;
    const dataUrl = typeof data === 'string' && /^data:image\/[a-z0-9.+-]+(?:;[^,]*)?,/i.test(data)
        ? data
        : null;
    const label = source === 'pdf-page-fallback'
        ? `PDF page ${page} whole-page recovery`
        : `PDF page ${page} embedded image ${imageId || index + 1}`;
    return {
        source: source as VisualCandidate['source'],
        page,
        imageId,
        width: finiteDimension(visual.width),
        height: finiteDimension(visual.height),
        label,
        dataUrl,
    };
}

function uniqueCandidates(values: unknown[]): {
    candidates: VisualCandidate[];
    invalidCount: number;
    duplicateCount: number;
} {
    const seen = new Map<string, VisualCandidate>();
    const output: VisualCandidate[] = [];
    let invalidCount = 0;
    let duplicateCount = 0;
    values.forEach((value, index) => {
        const candidate = normaliseVisual(value, index);
        if (!candidate) {
            invalidCount += 1;
            return;
        }
        const key = `${candidate.source}\u0000${candidate.page}\u0000${candidate.imageId}`;
        const prior = seen.get(key);
        if (prior) {
            if (prior.dataUrl === candidate.dataUrl
                && prior.width === candidate.width
                && prior.height === candidate.height) {
                duplicateCount += 1;
            } else {
                // One durable identity cannot truthfully refer to two different
                // transient derivatives. Keep the first and fail coverage closed.
                invalidCount += 1;
            }
            return;
        }
        seen.set(key, candidate);
        output.push(candidate);
    });
    return { candidates: output, invalidCount, duplicateCount };
}

function sortedPages(values: Iterable<number>): number[] {
    return [...new Set(values)].sort((a, b) => a - b);
}

function buildPrompt(evidence: DurableVisualIdentity[]): string {
    const manifest = evidence.map((item, index) => (
        `${index + 1}. ${item.label} (${item.width || '?'}x${item.height || '?'})`
    )).join('\n');
    // CN5 (Peter, 2026-10-13: "file names shouldnt dictate anything. only the
    // contents."): the vision model is not told the PDF's file name.
    return `You are reading visual evidence extracted from the construction reference PDF.

These are document visuals, not independently supplied site photos. Read every supplied image in order. Record only visible, quote-relevant facts: defect/location observations, measurements, annotations, captions, marked-up areas, access constraints, materials and visible condition. Do not invent causation, scope or quantities that are not visible. Preserve uncertainty.

Return plain text with exactly one section for every manifest item, using its exact label as the heading. Never omit an item, even if it is only a logo or has no quote-relevant content; say so explicitly.

VISUAL MANIFEST
${manifest}`;
}

function headingMatchesLabel(line: string, label: string): boolean {
    const withoutMarkdownHeading = line.trim().replace(/^#{1,6}\s+/, '');
    if (!withoutMarkdownHeading.startsWith(label)) return false;
    const remainder = withoutMarkdownHeading.slice(label.length);
    return remainder === '' || /^\s*(?::|[-–—])(?:\s|$)/.test(remainder);
}

function isPdfVisualHeading(line: string): boolean {
    const withoutMarkdownHeading = line.trim().replace(/^#{1,6}\s+/, '');
    return /^PDF page [1-9]\d*(?:(?: whole-page recovery)|(?: embedded image\b.*?))?(?:\s*$|\s*(?::|[-–—])(?:\s|$))/i.test(withoutMarkdownHeading);
}

function labelledVisualSections(text: string, evidence: DurableVisualIdentity[]): {
    findings: LooseRecord[];
    analysed: DurableVisualIdentity[];
    failed: LooseRecord[];
} {
    const lines = text.split(/\r?\n/);
    const headings: Array<{ line: number; evidenceIndex: number }> = [];
    for (let line = 0; line < lines.length; line += 1) {
        const lineText = lines[line];
        if (lineText === undefined) continue;
        let matchedExpectedHeading = false;
        for (let evidenceIndex = 0; evidenceIndex < evidence.length; evidenceIndex += 1) {
            const item = evidence[evidenceIndex];
            if (item && headingMatchesLabel(lineText, item.label)) {
                headings.push({ line, evidenceIndex });
                matchedExpectedHeading = true;
            }
        }
        if (!matchedExpectedHeading && isPdfVisualHeading(lineText)) {
            return {
                findings: [],
                analysed: [],
                failed: evidence.map(item => ({
                    ...item,
                    reason: 'visual analysis returned an unexpected labelled section',
                })),
            };
        }
    }
    headings.sort((left, right) => left.line - right.line);

    const findings: LooseRecord[] = [];
    const analysed: DurableVisualIdentity[] = [];
    const failed: LooseRecord[] = [];
    evidence.forEach((item, evidenceIndex) => {
        const matches = headings.filter(heading => heading.evidenceIndex === evidenceIndex);
        if (matches.length !== 1) {
            failed.push({
                ...item,
                reason: matches.length === 0
                    ? 'visual analysis omitted the required exact labelled section'
                    : 'visual analysis repeated the required exact labelled section',
            });
            return;
        }
        const matchedHeading = matches[0];
        if (!matchedHeading) return;
        const start = matchedHeading.line;
        const nextHeading = headings.find(heading => heading.line > start);
        const section = cleanText(lines.slice(start, nextHeading?.line ?? lines.length).join('\n'));
        const firstLine = (lines[start] || '').trim().replace(/^#{1,6}\s+/, '');
        const inlineBody = firstLine.slice(item.label.length)
            .replace(/^\s*(?::|[-–—])\s*/, '')
            .trim();
        const followingBody = lines.slice(start + 1, nextHeading?.line ?? lines.length).join('\n').trim();
        if (!section || (!inlineBody && !followingBody)) {
            failed.push({ ...item, reason: 'visual analysis returned an empty labelled section' });
            return;
        }
        analysed.push(item);
        findings.push({
            pages: [item.page],
            imageIds: [item.imageId],
            visuals: [item],
            text: section,
        });
    });
    return { findings, analysed, failed };
}

export function validatePdfVisionPageSections({
    text,
    pages = [],
}: {
    text?: unknown;
    pages?: unknown[];
} = {}) {
    const pageNumbers = sortedPages(
        (Array.isArray(pages) ? pages : [])
            .map(positivePage)
            .filter((page): page is number => page !== null),
    );
    const safeText = cleanText(text);
    if (safeText === INLINE_MEDIA_OMITTED) {
        return {
            validatedPages: [],
            failedPages: pageNumbers.map(page => ({
                page,
                reason: 'provider response contained inline media and was discarded',
            })),
            groundedText: '',
        };
    }
    const evidence: DurableVisualIdentity[] = pageNumbers.map(page => ({
        source: 'pdf-page-fallback',
        page,
        imageId: `prior-page-${page}`,
        width: null,
        height: null,
        label: `PDF page ${page}`,
    }));
    const labelled = labelledVisualSections(safeText, evidence);
    return {
        validatedPages: labelled.analysed.map(item => item.page),
        failedPages: labelled.failed.map(item => ({ page: item.page, reason: item.reason })),
        groundedText: labelled.findings.map(finding => finding.text).join('\n\n'),
    };
}

function normaliseUpstreamFailure(value: unknown, index: number): LooseRecord {
    const failure = value && typeof value === 'object' && !Array.isArray(value)
        ? value as LooseRecord
        : {};
    return {
        stage: cleanText(failure.stage || 'pdf-visual-import').slice(0, 80),
        code: cleanText(failure.code || `visual_failure_${index + 1}`).slice(0, 80),
        reason: compactError(failure.reason || failure.error || value),
        pages: sortedPages(
            (Array.isArray(failure.pages) ? failure.pages : [failure.page])
                .map(positivePage)
                .filter((page): page is number => page !== null),
        ),
    };
}

export function createPdfDocumentVisualFailureReceipt({
    stage = 'pdf-visual-import',
    error = 'PDF visual evidence could not be analysed',
}: {
    stage?: unknown;
    error?: unknown;
} = {}): LooseRecord {
    const upstreamFailure = normaliseUpstreamFailure({ stage, error }, 0);
    return {
        schemaVersion: 1,
        lane: 'pdf-document-visual-evidence',
        status: 'failed',
        discoveredVisuals: 0,
        invalidVisuals: 0,
        duplicateVisualsIgnored: 0,
        coveredByPriorVisionPages: [],
        embeddedImagesSupersededByPageRender: 0,
        duplicatePageFallbacksIgnored: 0,
        analysedVisuals: [],
        failedVisuals: [],
        upstreamFailures: [upstreamFailure],
        findings: [],
    };
}

export type PdfDocumentVisualEvidenceResult = {
    contentAppendix: string;
    receipt: LooseRecord;
};

/**
 * Consume every unresolved PDF visual and return durable textual evidence.
 * `analyseBatch` is dependency injected so the browser caller owns the AI
 * gateway/model policy while this traversal stays deterministic and testable.
 */
export async function analysePdfDocumentVisualEvidence({
    documentName = 'document.pdf',
    visuals = [],
    alreadyVisionAnalysedPages = [],
    upstreamFailures = [],
    analyseBatch,
}: {
    documentName?: string;
    visuals?: unknown[];
    alreadyVisionAnalysedPages?: unknown[];
    upstreamFailures?: unknown[];
    analyseBatch?: (input: {
        prompt: string;
        images: string[];
        evidence: DurableVisualIdentity[];
    }) => Promise<unknown>;
} = {}): Promise<PdfDocumentVisualEvidenceResult> {
    // CN5: `documentName` is still accepted so callers need not change, but
    // it is never written into the prompt or the saved appendix.
    void documentName;
    const rawVisuals = Array.isArray(visuals) ? visuals : [];
    const upstreamFailureReceipts = (Array.isArray(upstreamFailures) ? upstreamFailures : [])
        .map(normaliseUpstreamFailure);
    const {
        candidates,
        invalidCount: invalidVisualCount,
        duplicateCount: duplicateVisualsIgnored,
    } = uniqueCandidates(rawVisuals);
    const alreadyCovered = new Set(
        (Array.isArray(alreadyVisionAnalysedPages) ? alreadyVisionAnalysedPages : [])
            .map(positivePage)
            .filter((page): page is number => page !== null),
    );
    const coveredByPriorVisionPages = sortedPages(
        candidates.filter(item => alreadyCovered.has(item.page)).map(item => item.page),
    );

    const unresolved = candidates.filter(item => !alreadyCovered.has(item.page));
    const fallbackPages = new Set(
        unresolved.filter(item => item.source === 'pdf-page-fallback').map(item => item.page),
    );
    let embeddedImagesSupersededByPageRender = 0;
    let duplicatePageFallbacksIgnored = 0;
    const selected: VisualCandidate[] = [];
    const selectedFallbackPages = new Set<number>();
    for (const candidate of unresolved) {
        if (candidate.source === 'pdf-embed' && fallbackPages.has(candidate.page)) {
            embeddedImagesSupersededByPageRender += 1;
            continue;
        }
        // One whole-page recovery image covers the page. Ignore an accidental
        // duplicate render rather than paying twice for identical evidence.
        if (candidate.source === 'pdf-page-fallback') {
            if (selectedFallbackPages.has(candidate.page)) {
                duplicatePageFallbacksIgnored += 1;
                continue;
            }
            selectedFallbackPages.add(candidate.page);
        }
        selected.push(candidate);
    }

    const analysedVisuals: DurableVisualIdentity[] = [];
    const failedVisuals: LooseRecord[] = [];
    const findings: LooseRecord[] = [];

    for (let start = 0; start < selected.length; start += VISION_BATCH_SIZE) {
        const batch = selected.slice(start, start + VISION_BATCH_SIZE);
        const missingBytes = batch.filter(item => !item.dataUrl);
        for (const item of missingBytes) {
            failedVisuals.push({
                ...durableIdentity(item),
                reason: 'transient visual bytes were unavailable for analysis',
            });
        }
        const ready = batch.filter(item => !!item.dataUrl);
        if (ready.length === 0) continue;
        const evidence = ready.map(durableIdentity);
        try {
            if (typeof analyseBatch !== 'function') {
                throw new Error('document visual analysis route is unavailable');
            }
            const text = cleanText(await analyseBatch({
                prompt: buildPrompt(evidence),
                images: ready.map(item => item.dataUrl as string),
                evidence,
            }));
            if (text === INLINE_MEDIA_OMITTED) {
                throw new Error('provider response contained inline media and was discarded');
            }
            if (!text || text === 'NO_TEXT_FOUND') {
                throw new Error('visual analysis returned no content');
            }
            const labelled = labelledVisualSections(text, evidence);
            analysedVisuals.push(...labelled.analysed);
            findings.push(...labelled.findings);
            failedVisuals.push(...labelled.failed);
        } catch (error) {
            const reason = compactError(error);
            // Keep WHY the batch failed, not only a sentence about it. A rate
            // limit or a paused key pool says nothing about these pages, and
            // the caller must be able to tell that apart from a page the model
            // genuinely could not read. Without it every cause looked the same
            // and a provider wobble was recorded as an unreadable document.
            const aiFailure = classifyAiServiceFailure(error);
            const errorCode = cleanText((error as { code?: unknown } | null)?.code || '').slice(0, 80);
            failedVisuals.push(...evidence.map(item => ({
                ...item,
                reason,
                ...(errorCode ? { errorCode } : {}),
                ...(aiFailure.fromService && aiFailure.kind
                    ? { aiServiceKind: aiFailure.kind, aiServiceTransient: aiFailure.transient }
                    : {}),
            })));
        }
    }

    const totalHandled = analysedVisuals.length
        + candidates.filter(item => alreadyCovered.has(item.page)).length
        + embeddedImagesSupersededByPageRender
        + duplicatePageFallbacksIgnored;
    const complete = failedVisuals.length === 0
        && upstreamFailureReceipts.length === 0
        && invalidVisualCount === 0
        && totalHandled >= candidates.length;
    const status = candidates.length === 0
        && invalidVisualCount === 0
        && upstreamFailureReceipts.length === 0
        ? 'not-required'
        : (complete
            ? 'complete'
            : (analysedVisuals.length > 0 || coveredByPriorVisionPages.length > 0 ? 'partial' : 'failed'));

    const receipt = {
        schemaVersion: 1,
        lane: 'pdf-document-visual-evidence',
        status,
        discoveredVisuals: candidates.length,
        invalidVisuals: invalidVisualCount,
        duplicateVisualsIgnored,
        coveredByPriorVisionPages,
        embeddedImagesSupersededByPageRender,
        duplicatePageFallbacksIgnored,
        analysedVisuals,
        failedVisuals,
        upstreamFailures: upstreamFailureReceipts,
        findings,
    };

    if (findings.length === 0) return { contentAppendix: '', receipt };
    const lines = [
        // CN5: this appendix is saved into the document text that every later
        // prompt reads, so it carries no file name.
        `<pdf_document_visual_evidence schema_version="1" coverage_status="${status}">`,
        `Durable visual findings: ${analysedVisuals.length}/${selected.length} unresolved visual(s) analysed. Pages already covered by prior PDF vision: ${coveredByPriorVisionPages.join(', ') || 'none'}.`,
    ];
    for (const finding of findings) {
        const labels = (finding.visuals as DurableVisualIdentity[]).map(item => item.label);
        lines.push(
            `<pdf_visual_finding pages="${finding.pages.join(',')}" image_ids="${finding.imageIds.map(escapeXmlAttribute).join(',')}">`,
            `Source visuals: ${labels.join('; ')}`,
            finding.text,
            '</pdf_visual_finding>',
        );
    }
    if (status !== 'complete') {
        lines.push('VISUAL COVERAGE IS PARTIAL. Do not claim this PDF was fully read; avoid conclusions that depend on failed visual evidence.');
    }
    lines.push('</pdf_document_visual_evidence>');
    return { contentAppendix: lines.join('\n'), receipt };
}

export function appendPdfDocumentVisualEvidence(content: unknown, appendix: unknown): string {
    const base = String(content || '').trim();
    const visualEvidence = cleanText(appendix);
    return [base, visualEvidence].filter(Boolean).join('\n\n');
}
